import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AppShell } from '@/app/layouts/app-shell'
import { AuthLayout } from '@/features/auth/layouts/auth-layout'
import { ForbiddenPage } from '@/app/pages/forbidden'
import { SupportOpenPage } from '@/app/pages/support-open'
import { RequireAccess } from '@/shared/rbac'
import { HomeRedirect } from '@/shared/components/home-redirect'
import { LegacySearchRedirect } from '@/features/search/components/legacy-search-redirect'
import { PageSkeleton } from '@/shared/components/patterns'

import { QueryResponsePage } from '@/features/public/pages/query-response'

/** Lazy-loaded feature chunks */
const WorkspaceRoutes = lazy(() => import('@/features/workspace/routes'))
const AuthRoutes = lazy(() => import('@/features/auth/routes'))
const ClientAdminRoutes = lazy(() => import('@/features/client-admin/routes'))
const EngineAdminRoutes = lazy(() => import('@/features/engine-admin/routes'))
const OperationsDashboardRoutes = lazy(() => import('@/features/operations-dashboard/routes'))
const BillingImpactRoutes = lazy(() => import('@/features/billing-impact/routes'))
const PerformanceDashboardPage = lazy(() =>
  import('@/features/coder-performance/pages/performance-dashboard').then(m => ({ default: m.PerformanceDashboardPage })),
)
const SystemAdminRoutes = lazy(() => import('@/features/system-admin/routes'))
const NotificationsRoutes = lazy(() => import('@/features/notifications/routes'))
const SettingsRoutes = lazy(() => import('@/features/settings/routes'))
const KbRoutes = lazy(() => import('@/features/kb/routes'))
const ClientsRoutes = lazy(() => import('@/features/clients/routes'))
const PromptConfigRoutes = lazy(() => import('@/features/prompt-config/routes'))
const PlatformConfigRoutes = lazy(() => import('@/features/platform-config/routes'))
const ClientConfigRoutes = lazy(() => import('@/features/client-config/routes'))
const AnnouncementsRoutes = lazy(() => import('@/features/announcements/routes'))
const AccessRoutes = lazy(() => import('@/features/access/routes'))
const InvoicingRoutes = lazy(() => import('@/features/invoicing/routes'))
const TimesheetPage = lazy(() =>
  import('@/features/invoicing/pages/timesheet').then((m) => ({ default: m.TimesheetPage })),
)

function LazyFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <div
        className="h-8 w-8 animate-spin rounded-full border-[3px] border-t-transparent"
        style={{ borderColor: 'var(--primary-200)', borderTopColor: 'transparent' }}
      />
    </div>
  )
}


export function AppRouter() {
  return (
    <Routes>
      {/* Auth routes — no app shell, uses AuthLayout */}
      <Route element={<AuthLayout />}>
        <Route path="auth/*" element={<Suspense fallback={<LazyFallback />}><AuthRoutes /></Suspense>} />
      </Route>

      {/* Error pages — standalone, no layout */}
      <Route path="403" element={<ForbiddenPage />} />
      <Route path="404" element={<Navigate to="/" replace />} />

      {/* Public off-platform provider response page (slice 10) — outside auth wrapper */}
      <Route path="q/:token" element={<QueryResponsePage />} />

      {/* Protected routes — AppShell with sidebar + header */}
      <Route element={<AppShell />}>
        {/* Role-aware home redirect */}
        <Route index element={<HomeRedirect />} />

        {/* Support Codio deep link — any authenticated user (eligibility is
            decided server-side by the handoff mint itself); AppShell above
            already bounces a logged-out visitor to /auth/login?next=… and
            back. Support Codio scope.md L28.5/L28.6. */}
        <Route path="support/open" element={<SupportOpenPage />} />

        {/* Workspace — Medical Coder + Super Admin */}
        <Route
          path="workspace/*"
          element={
            <RequireAccess gate="canAccessWorkspace">
              <Suspense fallback={<LazyFallback />}><WorkspaceRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Client Admin — Client Admin + Super Admin */}
        <Route
          path="client-admin/*"
          element={
            <RequireAccess gate="canAdminClient">
              <Suspense fallback={<LazyFallback />}><ClientAdminRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Engine Admin — Engine Admin + Super Admin */}
        <Route
          path="engine-admin/*"
          element={
            <RequireAccess gate="canAdminEngine">
              <Suspense fallback={<LazyFallback />}><EngineAdminRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Coder Performance Dashboard — same gate as the Charts Dashboard it used to
            be a tab of: charts_dashboard:read:own, which is also what every
            /coder-performance endpoint requires. Coders hold it too and get the
            self-view. */}
        <Route
          path="performance-dashboard"
          element={
            <RequireAccess gate="canAdminClient">
              <Suspense fallback={<LazyFallback />}><PerformanceDashboardPage /></Suspense>
            </RequireAccess>
          }
        />

        {/* Operations Dashboard — Super Admin (pipeline run-health) */}
        <Route
          path="operations-dashboard/*"
          element={
            <RequireAccess gate="canViewOperations">
              <Suspense fallback={<LazyFallback />}><OperationsDashboardRoutes /></Suspense>
            </RequireAccess>
          }
        />


        {/* Billing Impact — client billed vs engine predicted. Internal-only
            in v1 (PRD A3): no client-facing grant exists for this module. */}
        <Route
          path="billing-impact/*"
          element={
            <RequireAccess when={(a) => a.can('billing_impact', 'read')}>
              <Suspense fallback={<LazyFallback />}><BillingImpactRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* System Admin — Super Admin only */}
        <Route
          path="system-admin/*"
          element={
            <RequireAccess gate="canAdminSystem">
              <Suspense fallback={<LazyFallback />}><SystemAdminRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Platform Configuration (Mandatory Fields) — gated on its own module so
            the route matches the sidebar link (any role holding mandatory_fields:read). */}
        <Route
          path="platform-config/*"
          element={
            <RequireAccess when={(a) => a.can('mandatory_fields', 'read')}>
              <Suspense fallback={<LazyFallback />}><PlatformConfigRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Clients (Utility) — consolidates client → facilities/providers/users into a single browsing experience */}
        <Route
          path="clients/*"
          element={
            <RequireAccess gate="canViewClients">
              <Suspense fallback={<LazyFallback />}><ClientsRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Prompt Config — Coding Manager + Engine Admin */}
        <Route
          path="prompt-config/*"
          element={
            <RequireAccess gate="canConfigurePrompts">
              <Suspense fallback={<LazyFallback />}><PromptConfigRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Client — Module Registry + Client Configuration. Engine Admin + Super Admin. */}
        <Route
          path="client-config/*"
          element={
            <RequireAccess gate="canConfigureClient">
              <Suspense fallback={<LazyFallback />}><ClientConfigRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Updates & Announcements — gated by canViewAnnouncements (updates read +
            the announcements_module feature flag). Hidden entirely when the flag is off. */}
        <Route
          path="announcements/*"
          element={
            <RequireAccess gate="canViewAnnouncements">
              <Suspense fallback={<LazyFallback />}><AnnouncementsRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Legacy redirect: the announcements route was renamed /updates → /announcements.
            Keep old bookmarks/links working (subpaths collapse to the hub). */}
        <Route path="updates" element={<Navigate to="/announcements" replace />} />
        <Route path="updates/*" element={<Navigate to="/announcements" replace />} />

        {/* Timesheet — its own module so coders reach it WITHOUT the invoicing
            grant (which carries client-confidential rates). */}
        <Route
          path="timesheet"
          element={
            <RequireAccess when={(a) => a.can('timesheet', 'read')}>
              <Suspense fallback={<LazyFallback />}><TimesheetPage /></Suspense>
            </RequireAccess>
          }
        />

        {/* Invoicing — coder rate cards. Gated on its own module (client_admin + super_admin). */}
        <Route
          path="invoicing/*"
          element={
            <RequireAccess when={(a) => a.can('invoicing', 'read')}>
              <Suspense fallback={<LazyFallback />}><InvoicingRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Access Request & Break-Glass. NOT every authenticated user any more:
            biller_admin had access_request revoked on 2026-08-06 (migration
            20260806_004) — it is Medicodio-wide by scope and holds no facility
            rows, so it has nothing to request. The gate below is what turns a
            direct URL into the no-access panel rather than a broken page. */}
        <Route
          path="access/*"
          element={
            <RequireAccess when={(a) => a.can('access_request', 'read')}>
              <Suspense fallback={<LazyFallback />}><AccessRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Search lives in the header panel; old /search bookmarks go home (?q= prefills the panel).
            Knowledge Base, Notifications & Settings — all authenticated users. */}
        <Route path="search/*" element={<LegacySearchRedirect />} />
        {/* KB uses the same page-shaped skeleton as its inner page-chunk
            Suspense (features/kb/routes.tsx PageFallback), so the two loading
            stages render identically instead of spinner → skeleton. */}
        <Route path="kb/*" element={<Suspense fallback={<PageSkeleton />}><KbRoutes /></Suspense>} />
        <Route path="notifications/*" element={<Suspense fallback={<LazyFallback />}><NotificationsRoutes /></Suspense>} />
        <Route path="settings/*" element={<Suspense fallback={<LazyFallback />}><SettingsRoutes /></Suspense>} />
      </Route>

      {/* Catch-all — unknown paths resolve through the role-aware root:
          logged-in users land on their own home (HomeRedirect), logged-out
          users hit the AppShell guard and bounce to /auth/login. Sending them
          straight to /workspace/queue instead would 403 any role without
          workspace access (e.g. engine-admin-only). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
